import { useSyncExternalStore } from 'react'

// Ticket sign-in flows (impersonation actor tokens, LTI launches, the password
// reset hand-off) finish with a full page navigation: `setActive` and then
// `window.location`. `setActive` flips Clerk's `isSignedIn` to true, so React
// gets at least one render pass as a signed-in user before the navigation
// commits. Guards use that pass to fire API calls, which the navigation then
// aborts with "Failed to fetch".
//
// This flag brackets that window so a guard can tell an aborted request apart
// from a real failure. Callers mark it only once the ticket has been accepted
// and the navigation is certain, and clear it if the flow fails afterwards, so
// a ticket that genuinely fails still surfaces to the user as normal.
//
// It is a store rather than a plain boolean because clearing it has to move
// consumers off whatever they rendered while it was set, and a module variable
// read during render gives React nothing to subscribe to.

let handoffPending = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

// A page restored from the bfcache brings this module back with its old value,
// so a hand-off that "ended" in a navigation can come back still marked.
function handlePageRestore(event: PageTransitionEvent): void {
  if (event.persisted) {
    clearTicketHandoffPending()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  window.addEventListener('pageshow', handlePageRestore)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      window.removeEventListener('pageshow', handlePageRestore)
    }
  }
}

function getSnapshot(): boolean {
  return handoffPending
}

/** Call immediately before the `setActive` that precedes the hard navigation. */
export function markTicketHandoffPending(): void {
  if (handoffPending) return
  handoffPending = true
  emit()
}

/** Call when the hand-off fails, so the navigation is no longer coming. */
export function clearTicketHandoffPending(): void {
  if (!handoffPending) return
  handoffPending = false
  emit()
}

/** True while a ticket sign-in is about to replace the current document. */
export function useTicketHandoffPending(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
